import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarDays, Wallet, Users, Package, RefreshCcw,
  LifeBuoy, CreditCard, Clock, AlertOctagon, Star, TrendingUp, ArrowUpRight,
} from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney, titleCase } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { KpiCard } from "../components/KpiCard";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { InfoTooltip } from "../components/InfoTooltip";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [recent, setRecent] = useState([]);
  const [pending, setPending] = useState({ suppliers: [], experiences: [] });
  const [urgent, setUrgent] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState([]);

  useEffect(() => {
    let active = true;
    const sections = [
      ["metrics", setMetrics], ["recent-bookings", setRecent],
      ["pending-approvals", setPending], ["urgent-issues", setUrgent], ["health", setHealth],
    ];
    Promise.allSettled(sections.map(([path]) => api.get(`/dashboard/${path}`)))
      .then((results) => {
        if (!active) return;
        const errors = [];
        results.forEach((result, index) => {
          if (result.status === "fulfilled") sections[index][1](result.value.data);
          else errors.push(sections[index][0]);
        });
        setFailed(errors);
        setLoading(false);
        if (errors.length) toast.error("Some dashboard data could not be loaded.");
      });
    return () => { active = false; };
  }, []);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Good day, ${user?.name?.split(" ")[0] || "team"}.`}
        description="Operational overview for the Voocly marketplace — booking value shown in USD."
      />

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <KpiCard label="Today's bookings" value={metrics?.today_bookings ?? "—"} icon={CalendarDays} info="Bookings created today (UTC)." testId="kpi-today-bookings" />
        <KpiCard label="Gross booking value" value={metrics ? fmtMoney(metrics.gross_booking_value_usd, "USD") : "—"} hint="All-time, USD" icon={Wallet} info="Total recorded USD customer payments, before refunds and fees." testId="kpi-gbv" />
        <KpiCard label="Pending suppliers" value={metrics?.pending_supplier_approvals ?? "—"} icon={Users} info="Suppliers awaiting approval — verify documents and approve to allow them to list experiences." testId="kpi-pending-suppliers" />
        <KpiCard label="Pending experiences" value={metrics?.pending_experience_approvals ?? "—"} icon={Package} info="Experience approval data is unavailable." testId="kpi-pending-experiences" />
        <KpiCard label="Refund requests" value={metrics?.refund_requests ?? "—"} icon={RefreshCcw} info="Refund requests awaiting review." testId="kpi-refund-requests" />
        <KpiCard label="Open support cases" value={metrics?.open_support_cases ?? "—"} icon={LifeBuoy} info="Cases not yet resolved or closed." testId="kpi-open-cases" />
        <KpiCard label="Failed payments" value={metrics?.failed_payments ?? "—"} icon={CreditCard} info="Unpaid checkout sessions with a recorded payment failure." testId="kpi-failed-payments" />
        <KpiCard label="Activities in 24h" value={metrics?.upcoming_activities_24h ?? "—"} icon={Clock} info="Confirmed bookings with a recorded UTC start in the next 24 hours." testId="kpi-upcoming" />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-8">
        <div className="lg:col-span-2 bg-card border border-border rounded-lg overflow-hidden" data-testid="recent-bookings-card">
          <div className="px-5 py-3.5 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold">Recent bookings</h3>
            <Link to="/bookings" className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="view-all-bookings-link">
              View all <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="text-left px-4 py-2 font-medium">Ref</th>
                  <th className="text-left px-4 py-2 font-medium">Customer</th>
                  <th className="text-left px-4 py-2 font-medium">Experience</th>
                  <th className="text-left px-4 py-2 font-medium">Amount</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((b) => (
                  <tr key={b.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <Link to={`/bookings/${b.id}`} className="hover:underline" data-testid={`recent-booking-${b.id}`}>{b.reference}</Link>
                    </td>
                    <td className="px-4 py-2.5">{b.customer_name}</td>
                    <td className="px-4 py-2.5 max-w-[260px] truncate">{b.experience_title}</td>
                    <td className="px-4 py-2.5 tabular-nums">{fmtMoney(b.amount_paid, b.currency)}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={b.status} /></td>
                  </tr>
                ))}
                {recent.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-muted-foreground">{loading ? "Loading…" : failed.includes("recent-bookings") ? "Unable to load recent bookings." : "No recent bookings yet."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg" data-testid="pending-approvals-card">
          <div className="px-5 py-3.5 border-b border-border">
            <h3 className="text-sm font-semibold">Pending approvals</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Suppliers & experiences awaiting review</p>
          </div>
          <div className="divide-y divide-border">
            {pending.suppliers.map((s) => (
              <Link key={s.id} to={`/suppliers/${s.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-muted/30" data-testid={`pending-supplier-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground">Supplier{s.country ? ` · ${s.country}` : ""}</div>
                </div>
                <StatusBadge status="pending" />
              </Link>
            ))}
            {pending.experiences.map((e) => (
              <Link key={e.id} to={`/experiences/${e.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-muted/30" data-testid={`pending-exp-${e.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground">Experience · {e.destination}</div>
                </div>
                <StatusBadge status="pending_review" />
              </Link>
            ))}
            {(pending.suppliers.length + pending.experiences.length) === 0 && (
              <div className="px-5 py-10 text-center text-xs text-muted-foreground">{loading ? "Loading…" : failed.includes("pending-approvals") ? "Unable to load pending approvals." : "No pending supplier approvals."}</div>
            )}
            {!loading && !failed.includes("pending-approvals") && pending.experience_approvals_available === false && <p className="px-5 py-3 text-xs text-muted-foreground">Experience approval data unavailable.</p>}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-lg" data-testid="urgent-issues-card">
          <div className="px-5 py-3.5 border-b border-border flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-[hsl(var(--status-danger-text))]" />
            <h3 className="text-sm font-semibold">Urgent issues</h3>
          </div>
          <div className="divide-y divide-border">
            {urgent.length === 0 && <div className="px-5 py-10 text-center text-xs text-muted-foreground">{loading ? "Loading…" : failed.includes("urgent-issues") ? "Unable to load urgent issues." : "No urgent cases right now."}</div>}
            {urgent.map((c) => (
              <Link key={c.id} to={`/support/${c.id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-muted/30" data-testid={`urgent-case-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.subject}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <span className="font-mono">{c.case_id}</span> · {titleCase(c.type)} · {c.customer_name}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <StatusBadge status={c.priority} variant={c.priority === "urgent" || c.priority === "high" ? "danger" : "warning"} label={titleCase(c.priority)} />
                  <StatusBadge status={c.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5" data-testid="health-card">
          <h3 className="text-sm font-semibold flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Marketplace health</h3>
          <div className="mt-4 space-y-3 text-sm">
            <HealthRow label="Cancellation rate" value={`${health?.cancellation_rate ?? "—"}%`} good={health?.cancellation_rate == null ? null : health.cancellation_rate < 10} />
            <HealthRow label="Avg review rating" value={`${health?.average_review_rating ?? "—"}`} good={health?.average_review_rating == null ? null : health.average_review_rating >= 4.0} icon={Star} />
            <HealthRow label="Supplier response rate" value={`${health?.supplier_response_rate ?? "—"}%`} good={health?.supplier_response_rate == null ? null : health.supplier_response_rate >= 80} info="Share of refund requests with a recorded supplier response." />
            <HealthRow label="Payout failures" value={`${health?.payout_failures ?? "—"}`} good={health?.payout_failures == null ? null : health.payout_failures === 0} info="Payout failure data is unavailable." />
          </div>
        </div>
      </section>
    </div>
  );
}

function HealthRow({ label, value, good, icon: Icon, info }) {
  return (
    <div className="flex items-center justify-between" data-testid={`health-row-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="text-xs text-muted-foreground flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}{label}{info && <InfoTooltip text={info} />}
      </span>
      <span className={`text-sm font-medium tabular-nums ${good == null ? "text-muted-foreground" : good ? "text-[hsl(var(--status-success-text))]" : "text-[hsl(var(--status-warning-text))]"}`}>{value}</span>
    </div>
  );
}
